/* eslint-disable */
import React, { useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { ShoppingBag } from 'lucide-react';
import { productsApi } from '../lib/api';
import { useCart } from '../context/CartContext';
import StarRating from '../components/Reviews/StarRating';

const CATEGORIES = [
  { key: '', label: 'ALL SHADES' },
  { key: 'matte', label: 'MATTE' },
  { key: 'satin', label: 'SATIN' },
  { key: 'sheer', label: 'SHEER' },
  { key: 'gloss', label: 'GLOSS' },
];

const CATEGORY_TITLES = {
  '': 'All Shades', matte: 'Matte', satin: 'Satin', sheer: 'Sheer', gloss: 'Gloss',
};

const Shop = () => {
  const [params, setParams] = useSearchParams();
  const category = params.get('category') || '';
  const [search, setSearch] = useState('');
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const { addItem } = useCart();

  useEffect(() => {
    setLoading(true);
    productsApi.list(category ? { category } : {})
      .then(setProducts)
      .catch(() => setProducts([]))
      .finally(() => setLoading(false));
  }, [category]);

  const filtered = useMemo(() => {
    if (!search.trim()) return products;
    const q = search.toLowerCase();
    return products.filter((p) => (p.name + ' ' + p.shade + ' ' + p.category).toLowerCase().includes(q));
  }, [products, search]);

  const setCategory = (c) => { if (c) setParams({ category: c }); else setParams({}); };

  const handleQuickAdd = (e, p) => {
    e.preventDefault();
    e.stopPropagation();
    addItem(p, 1);
  };

  return (
    <main className="bg-[#f5f1e6] text-[#1a1410] min-h-screen">
      <div className="px-6 lg:px-10 pt-6 pb-3">
        <div className="text-[10px] tracking-[0.28em] text-[#3a2e26] border-t border-[#1a1410] pt-2 inline-block">
          THE BOUTIQUE
        </div>
        <h1 className="font-serif text-[28px] lg:text-[36px] leading-tight mt-2">
          {CATEGORY_TITLES[category]}
        </h1>
      </div>

      <div className="px-6 lg:px-10 pb-3 border-b border-[#e5dfd0] flex flex-wrap items-center justify-between gap-3">
        <div className="flex flex-wrap items-center gap-5">
          {CATEGORIES.map((c) => (
            <button key={c.key || 'all'} onClick={() => setCategory(c.key)}
              className={`text-[10px] tracking-[0.22em] pb-1 transition-colors ${
                category === c.key ? 'text-[#1a1410] border-b border-[#1a1410]' : 'text-[#7a6a5a] hover:text-[#1a1410]'
              }`}>
              {c.label}
            </button>
          ))}
        </div>
        <div className="w-full md:w-64">
          <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search the edit…"
            className="w-full bg-transparent border-b border-[#cdc2b1] focus:border-[#1a1410] py-1 text-xs focus:outline-none placeholder:text-[#a89784]" />
        </div>
      </div>

      <div className="px-6 lg:px-10 py-5">
        {loading ? (
          <p className="text-center text-[#7a6a5a] py-12">Loading…</p>
        ) : filtered.length === 0 ? (
          <p className="text-center text-[#7a6a5a] py-12">No shades match that search.</p>
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-x-3 gap-y-5">
            {filtered.map((p) => (
              <article key={p.id} className="group flex flex-col">
                <Link to={`/product/${p.id}`} className="relative block overflow-hidden bg-[#e9e2d3] aspect-[4/3]">
                  <img
                    src={p.image}
                    alt={p.name}
                    className="w-full h-full object-cover transition-transform duration-[900ms] group-hover:scale-[1.04]"
                    loading="lazy"
                  />
                  <button
                    onClick={(e) => handleQuickAdd(e, p)}
                    aria-label={`Add ${p.name} to bag`}
                    title="Quick add to bag"
                    className="absolute bottom-1.5 right-1.5 w-8 h-8 bg-[#1a1410] text-[#f5f1e6] flex items-center justify-center opacity-0 group-hover:opacity-100 translate-y-2 group-hover:translate-y-0 transition-all duration-300 hover:bg-black"
                  >
                    <ShoppingBag className="w-3.5 h-3.5" strokeWidth={1.4} />
                  </button>
                </Link>

                <div className="mt-2 flex flex-col gap-1">
                  <div className="flex items-center justify-between gap-2">
                    <span className="text-[9px] tracking-[0.22em] uppercase text-[#7a6a5a]">{p.category}</span>
                    <span
                      className="inline-block w-2 h-2 rounded-full border border-[#cdc2b1]"
                      style={{ backgroundColor: p.swatch }}
                      aria-hidden="true"
                    />
                  </div>

                  <Link to={`/product/${p.id}`} className="font-serif text-[15px] leading-tight text-[#1a1410] hover:opacity-80 line-clamp-1">
                    {p.name}
                  </Link>

                  {p.review_count > 0 ? (
                    <Link to={`/product/${p.id}#reviews-section`} className="inline-flex items-center gap-1.5 hover:opacity-80">
                      <StarRating value={p.avg_rating || 0} size={10} />
                      <span className="text-[10px] text-[#7a6a5a]">
                        {p.avg_rating?.toFixed(1)} <span className="text-[#a89784]">({p.review_count})</span>
                      </span>
                    </Link>
                  ) : (
                    <div className="inline-flex items-center gap-1.5">
                      <StarRating value={0} size={10} />
                      <span className="text-[10px] text-[#a89784]">No reviews</span>
                    </div>
                  )}

                  <div className="font-medium text-[13px] text-[#1a1410] tabular-nums">Rs. {p.price.toFixed(2)}</div>

                  <button
                    onClick={(e) => handleQuickAdd(e, p)}
                    className="mt-1 w-full border border-[#1a1410] text-[#1a1410] text-[9px] tracking-[0.26em] py-1.5 hover:bg-[#1a1410] hover:text-[#f5f1e6] transition-colors"
                  >
                    ADD TO BAG
                  </button>
                </div>
              </article>
            ))}
          </div>
        )}
      </div>
    </main>
  );
};

export default Shop;
